import { useState } from "react";
import { Hand, HandMetal } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { useClaimTenderPricing } from "../hooks/useTenders";
import { ACTION_STATUS_COLORS, tenderActionLabel, type Tender } from "../types";
import { extractTenderError } from "../utils";

interface Props {
  tender: Tender;
}

/** Badge „Do zrobienia" z osobą w nawiasie + „Przejmij" / „Zwolnij" wyliczanie wyceny.
 *
 *  Przejęcie mówi reszcie zespołu, kto liczy wycenę tej tury, żeby dwie osoby nie robiły
 *  tego samego: od tej chwili listy pokazują „Przejęte (Imię Nazwisko)" zamiast „Czeka na
 *  wyliczenie wyceny". Gdy turę przejął już ktoś inny, backend odpowiada 409 — pokazujemy
 *  komunikat i „Przejmij mimo to" (np. gdy autor przejęcia jest na urlopie). */
export function TenderPricingClaimControl({ tender }: Props) {
  const user = useAuthStore((s) => s.user);
  const claim = useClaimTenderPricing(tender.id);
  const [error, setError] = useState<string | null>(null);
  const [offerForce, setOfferForce] = useState(false);

  const canCalculatePricing = !!user?.has_calculate_pricing_permission;
  const isManager = !!user?.is_ceo || user?.role === "director" || user?.role === "coordinator";
  const status = tender.action_status;
  if (!status) return null;

  const claimedByMe = status === "pricing_claimed" && tender.pricing_claimed_by === user?.id;
  const canClaim = canCalculatePricing && status === "needs_pricing";
  const canTakeOver = canCalculatePricing && status === "pricing_claimed" && !claimedByMe;
  const canRelease = status === "pricing_claimed" && (claimedByMe || isManager);

  const run = (arg: { action: "claim"; force?: boolean } | { action: "release" }) => {
    setError(null);
    claim.mutate(arg, {
      onSuccess: () => setOfferForce(false),
      onError: (err) => {
        setError(extractTenderError(err));
        // 409 = ktoś inny przejął w międzyczasie — daj wybór „mimo to".
        setOfferForce(arg.action === "claim" && (err as { response?: { status?: number } })?.response?.status === 409);
      },
    });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_STATUS_COLORS[status]}`}
        title={tender.pricing_claimed_by_name ? `Wycenę liczy: ${tender.pricing_claimed_by_name}` : undefined}
      >
        {tenderActionLabel(status, tender.pricing_claimed_by_name)}
      </span>
      {canClaim && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => run({ action: "claim" })}
          loading={claim.isPending}
          title={"Zaznacz, że to Ty liczysz wycenę — inni zobaczą „Przejęte (Twoje nazwisko)\u201d"}
        >
          <Hand size={13} /> Przejmij
        </Button>
      )}
      {canTakeOver && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => run({ action: "claim", force: true })}
          loading={claim.isPending}
          title={`Wycenę przejął ${tender.pricing_claimed_by_name ?? "ktoś inny"} — przejmij ją na siebie`}
        >
          <HandMetal size={13} /> Przejmij mimo to
        </Button>
      )}
      {canRelease && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => run({ action: "release" })}
          loading={claim.isPending}
          title={"Cofnij przejęcie — przetarg wróci do „Czeka na wyliczenie wyceny\u201d"}
        >
          Zwolnij
        </Button>
      )}
      {error && (
        <span className="text-xs text-red-600 dark:text-red-400 inline-flex items-center gap-2">
          {error}
          {offerForce && canCalculatePricing && (
            <button
              type="button"
              className="underline hover:no-underline"
              onClick={() => run({ action: "claim", force: true })}
            >
              Przejmij mimo to
            </button>
          )}
        </span>
      )}
    </div>
  );
}
